import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import {
    AuditLogChangeType,
    HistoryDetailsItem,
    ResourceType,
} from "@common/types/auditLog";
import { AccrualMatchAuditSnapshot } from "@common/schemas/domains/accrualReview/accrual-match-audit-log.schema";
import { useHistoryDrawer } from "../../historyDrawerContext";
import { AccrualMatchCard } from "./AccrualMatchCard";
import { AccrualMatchAuditViewProvider } from "./AccrualMatchAuditViewProvider";
import { ACCRUAL_MATCH_DIFF_FIELD, parseAccrualMatchSnapshot } from "./schema";
import styles from "./HistoryAccrualMatchDetails.module.scss";

interface Props {
    data: HistoryDetailsItem[];
    footer: React.ReactNode;
}

interface CardInput {
    id: string;
    sourceId: string | null;
    snapshot: AccrualMatchAuditSnapshot;
    isMatched: boolean;
    isReverted: boolean;
}

function extractCards(data: HistoryDetailsItem[]): CardInput[] {
    const cards: CardInput[] = [];
    const seen = new Set<string>();

    for (const log of data) {
        if (log.resourceType !== ResourceType.ACCRUAL_MATCH) {
            continue;
        }
        const diff = log.diff.find((d) => d.field === ACCRUAL_MATCH_DIFF_FIELD);
        if (!diff) {
            continue;
        }
        const isMatched = log.changeType === AuditLogChangeType.CREATE;
        const snapshotValue = isMatched
            ? diff.after?.value
            : diff.before?.value;
        const snapshot = parseAccrualMatchSnapshot(snapshotValue);
        if (!snapshot) {
            continue;
        }
        const key = `${log.resourceId}:${log.changeType}`;
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        cards.push({
            id: log.id,
            sourceId: log.sourceId ?? null,
            snapshot,
            isMatched,
            isReverted: log.revertedAt != null,
        });
    }

    return cards;
}

/**
 * Maximum time (ms) we keep "the user just clicked revert" sticky so the
 * subsequent cards refetch can trigger an auto-close. After this window
 * expires the ref clears itself, preventing the drawer from auto-closing
 * later in response to unrelated background updates.
 */
const PENDING_CLOSE_WINDOW_MS = 5_000;

export const HistoryAccrualMatchDetails: React.FC<Props> = ({
    data,
    footer,
}) => {
    const cards = useMemo(() => extractCards(data), [data]);
    const { closeDrawer } = useHistoryDrawer();
    /**
     * Set when the user clicks revert/redo on one of the cards. The effect
     * below watches the recomputed `cards` and only closes the drawer when
     * all remaining cards are reverted (or the list is empty), avoiding
     * accidental closes from background refetches.
     */
    const pendingCloseRef = useRef(false);
    const pendingCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );

    const clearPendingClose = useCallback(() => {
        pendingCloseRef.current = false;
        if (pendingCloseTimerRef.current !== null) {
            clearTimeout(pendingCloseTimerRef.current);
            pendingCloseTimerRef.current = null;
        }
    }, []);

    const handleAfterRevert = useCallback(() => {
        pendingCloseRef.current = true;
        if (pendingCloseTimerRef.current !== null) {
            clearTimeout(pendingCloseTimerRef.current);
        }
        // If the cards data doesn't reflect the revert within the window
        // (eventual consistency, debounced invalidation, etc.) drop the
        // pending state so a much later refetch can't surprise-close the
        // drawer.
        pendingCloseTimerRef.current = setTimeout(() => {
            pendingCloseRef.current = false;
            pendingCloseTimerRef.current = null;
        }, PENDING_CLOSE_WINDOW_MS);
    }, []);

    useEffect(() => {
        if (!pendingCloseRef.current) {
            return;
        }
        if (cards.length === 0 || cards.every((c) => c.isReverted)) {
            clearPendingClose();
            closeDrawer();
        }
    }, [cards, closeDrawer, clearPendingClose]);

    // Always clear the pending timer on unmount so we never call setState
    // / closeDrawer after the component has gone away.
    useEffect(
        () => () => {
            if (pendingCloseTimerRef.current !== null) {
                clearTimeout(pendingCloseTimerRef.current);
                pendingCloseTimerRef.current = null;
            }
        },
        [],
    );

    if (cards.length === 0) {
        return <EmptyState header="No accrual matches found" />;
    }

    return (
        <AccrualMatchAuditViewProvider>
            <div className={styles.container}>
                <div className={styles.cardsContainer}>
                    {cards.map((card) => (
                        <AccrualMatchCard
                            key={card.id}
                            sourceId={card.sourceId}
                            snapshot={card.snapshot}
                            isMatched={card.isMatched}
                            isReverted={card.isReverted}
                            onAfterRevert={handleAfterRevert}
                        />
                    ))}
                </div>
                {footer}
            </div>
        </AccrualMatchAuditViewProvider>
    );
};
